import React, { useState } from 'react'
import cn from 'classnames'
import { Check, Download } from 'lucide-react'
import styles from './profile.module.scss'
import { downloadWalletBackup } from 'lib/walletBackup'

// Body of the delete-account confirm. When Treechat holds wallet keys for the
// account, the key file comes first: the erase deletes those keys.
export default function DeleteAccountConfirmMessage({
    walletBackupSource,
    userId,
    onWalletBackedUp,
    passwordRef,
}) {
    // A native share sheet closing is not proof the file was saved.
    const [backupState, setBackupState] = useState('idle')
    const [backupError, setBackupError] = useState(null)

    const downloadKeys = async () => {
        if (backupState === 'saving') return
        setBackupState('saving')
        setBackupError(null)
        try {
            const delivery = await downloadWalletBackup(walletBackupSource, userId)
            if (delivery === 'native_share') {
                setBackupState('shared')
            } else {
                setBackupState('saved')
                onWalletBackedUp()
            }
        } catch (error) {
            console.error('Error downloading wallet keys:', error)
            setBackupState(walletBackupSource === 'client_shares' ? 'unavailable' : 'idle')
            setBackupError(
                error instanceof Error && error.message
                    ? error.message
                    : 'Could not download your wallet keys.',
            )
        }
    }

    const confirmKeysSaved = () => {
        setBackupState('saved')
        onWalletBackedUp()
    }

    const acceptWalletLoss = () => {
        setBackupState('accepted_loss')
        onWalletBackedUp()
    }

    return (
        <div className={styles.deleteAccountConfirmMessage}>
            <p>
                Your profile, posts and threads will be removed from Treechat and you will be signed
                out. After 30 days your account data is erased for good.
            </p>
            {walletBackupSource ? (
                <div className={styles.walletBackupStep}>
                    <p className={styles.deleteAccountNote}>
                        First, download your wallet keys. Erasing your account also deletes the keys
                        Treechat holds, and this file is how you reach any bitcoin in your wallet.
                    </p>
                    <button
                        type="button"
                        className={cn(
                            styles.walletBackupButton,
                            (backupState === 'saved' || backupState === 'accepted_loss') &&
                                styles.walletBackupDone,
                        )}
                        onClick={downloadKeys}
                        disabled={
                            backupState === 'saving' ||
                            backupState === 'saved' ||
                            backupState === 'accepted_loss'
                        }
                        data-testid="delete-account-download-keys"
                    >
                        {backupState === 'saved' || backupState === 'accepted_loss' ? (
                            <Check size={16} />
                        ) : (
                            <Download size={16} />
                        )}
                        {backupState === 'saved' || backupState === 'accepted_loss'
                            ? 'Wallet step completed'
                            : backupState === 'saving'
                            ? 'Downloading…'
                            : backupState === 'shared'
                            ? 'Share wallet keys again'
                            : 'Download wallet keys'}
                    </button>
                    {backupError && (
                        <div className={styles.walletBackupError} role="alert">
                            {backupError}
                        </div>
                    )}
                    {backupState === 'shared' && (
                        <>
                            <p className={styles.deleteAccountNote}>
                                Closing the share sheet does not save the file. Confirm only after
                                you have saved the wallet keys to a safe place.
                            </p>
                            <button
                                type="button"
                                className={styles.walletBackupButton}
                                onClick={confirmKeysSaved}
                                data-testid="delete-account-confirm-wallet-saved"
                            >
                                I saved my wallet keys
                            </button>
                        </>
                    )}
                    {(backupState === 'unavailable' || backupState === 'accepted_loss') && (
                        <>
                            <p className={styles.walletBackupError}>
                                If you delete without a wallet key backup, you may permanently lose
                                access to any bitcoin in your wallet. Try the browser where you set
                                up self-custody, or use a backup you already saved.
                            </p>
                            {backupState === 'unavailable' && (
                                <button
                                    type="button"
                                    className={styles.walletBackupButton}
                                    onClick={acceptWalletLoss}
                                    data-testid="delete-account-acknowledge-wallet-loss"
                                >
                                    I understand I may lose access to my bitcoin
                                </button>
                            )}
                        </>
                    )}
                </div>
            ) : (
                <p className={styles.deleteAccountNote}>You will not be able to sign in again.</p>
            )}
            {passwordRef && (
                <input
                    className="form-control"
                    type="password"
                    autoComplete="current-password"
                    placeholder="Current password"
                    aria-label="Current password"
                    data-testid="delete-account-current-password"
                    onChange={e => {
                        passwordRef.current = e.target.value
                    }}
                />
            )}
        </div>
    )
}
